import { Modal, ModalTitle } from '@cian/ui-kit';
import * as React from 'react';
import { Content } from './parts/Content';
import { ContentHeader } from './parts/ContentHeader';
import { ContentFooter } from './parts/ContentFooter/ContentFooter';
import { ErrorContent } from './parts/ErrorContent';
import { AiSummaryLoadingBody } from './parts/LoadingBody';
import type { IAiSummaryFooterActions, IAiSummaryResponse } from './types';
import { ELoadingStatus } from '../../types/redux/loadingStatus';
import * as styles from './styles.css';
import clsx from 'clsx';

type TVisualState = 'loading' | 'revealing' | 'ready';

export interface IAiSummaryModalDesktopProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  summary: IAiSummaryResponse | null;
  status: ELoadingStatus;
  requestId: number;
  onReload: () => void;
  footerActions: IAiSummaryFooterActions[];
  onCallButtonSlotChange?(newbuildingId: number, slot: HTMLDivElement | null): void;
}

export const AiSummaryModalDesktop = ({
  open,
  onOpenChange,
  summary,
  status,
  requestId,
  onReload,
  footerActions,
  onCallButtonSlotChange,
}: IAiSummaryModalDesktopProps): React.JSX.Element | null => {
  const [activeNewbuildingId, setActiveNewbuildingId] = React.useState<number | null>(null);
  const [completedRequestId, setCompletedRequestId] = React.useState<number | null>(() =>
    status === ELoadingStatus.Initial || status === ELoadingStatus.Loading ? null : requestId,
  );
  const [visualRequestId, setVisualRequestId] = React.useState(requestId);
  const [visualState, setVisualState] = React.useState<TVisualState>(() =>
    status === ELoadingStatus.Succeed ? 'ready' : 'loading',
  );
  const isLoading =
    status === ELoadingStatus.Initial || status === ELoadingStatus.Loading || completedRequestId !== requestId;
  const activeSummary = summary?.summarization.find(item => item.id === activeNewbuildingId);
  const activeFooterActions = footerActions.find(item => item.newbuildingId === activeNewbuildingId);

  React.useEffect(() => {
    const firstNewbuildingId = summary?.summarization[0]?.id;

    if (firstNewbuildingId && !summary.summarization.some(item => item.id === activeNewbuildingId)) {
      setActiveNewbuildingId(firstNewbuildingId);
    }
  }, [activeNewbuildingId, summary]);

  const handleLoadingComplete = React.useCallback(() => setCompletedRequestId(requestId), [requestId]);

  React.useEffect(() => {
    if (requestId !== visualRequestId) {
      setVisualRequestId(requestId);
      setVisualState('loading');

      return;
    }

    if (status === ELoadingStatus.Succeed && completedRequestId === requestId && visualState === 'loading') {
      setVisualState('revealing');
    }
  }, [completedRequestId, requestId, status, visualRequestId, visualState]);

  const handleResultAnimationEnd = React.useCallback(
    (event: React.AnimationEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget && visualState === 'revealing') {
        setVisualState('ready');
      }
    },
    [visualState],
  );

  if (isLoading || visualState === 'loading') {
    return (
      <Modal
        header={<ModalTitle>Итоги сравнения от ИИ</ModalTitle>}
        body={<AiSummaryLoadingBody key={requestId} onComplete={handleLoadingComplete} />}
        onOpenChange={onOpenChange}
        open={open}
        title="Итоги сравнения от ИИ"
        width={640}
      />
    );
  }

  if (status === ELoadingStatus.Failed) {
    return (
      <Modal
        header={<ModalTitle>Итоги сравнения от ИИ</ModalTitle>}
        body={<ErrorContent onReload={onReload} />}
        onOpenChange={onOpenChange}
        open={open}
        title="Итоги сравнения от ИИ"
        width={640}
      />
    );
  }

  if (!summary || !activeSummary) {
    return null;
  }

  return (
    <Modal
      onOpenChange={onOpenChange}
      open={open}
      width={640}
      header={
        <ContentHeader
          activeNewbuildingId={activeSummary.id}
          items={summary.summarization}
          onSelectNewbuilding={setActiveNewbuildingId}
          title={summary.title}
        />
      }
      title={summary.title}
      body={
        <div className={visualState === 'revealing' ? styles['viewport-revealing'] : undefined}>
          {visualState === 'revealing' && (
            <div className={clsx(styles['loading-layer'], styles['loading-exiting'])}>
              <AiSummaryLoadingBody key={requestId} onComplete={handleLoadingComplete} />
            </div>
          )}
          <div
            className={clsx(
              styles['result-layer'],
              visualState === 'revealing' ? styles['result-revealing'] : styles['result-ready'],
            )}
          >
            <div
              className={clsx(
                styles['result-card'],
                visualState === 'revealing' ? styles['result-card-revealing'] : styles['result-card-ready'],
              )}
              data-testid="ai-summary-result-card"
              onAnimationEnd={handleResultAnimationEnd}
            >
              <Content emptyState={summary.emptyState} summary={activeSummary} />
            </div>
          </div>
        </div>
      }
      footer={
        <ContentFooter
          actionText={summary.disclaimer.actionText}
          activeNewbuildingId={activeSummary.id}
          chat={activeFooterActions?.chat}
          onCallButtonSlotChange={onCallButtonSlotChange}
          subtitle={summary.disclaimer.subtitle}
          title={summary.disclaimer.title}
        />
      }
    />
  );
};
